/**
 * WildfireLayer.tsx — MapLibre version
 * طبقة الحرائق — صور لهب واقعية (راجع wildfireIcons.ts) بلا خلفية ولا هالات مصطنعة، تُختار
 * حسب شدّة كل بؤرة. تُحدَّث البؤر عند تحريك الخريطة (moveend) لتغطية النطاق الظاهر.
 *
 * ── لماذا أُعيدت هندسة الرسم ───────────────────────────────────────────────────
 * النسخة السابقة كانت تُنشئ عنصراً لكل بؤرة يرجعها الباك إند (حتى 500)، وتكتب لكل واحد
 * `left/top` في كل تغيّر كاميرا. وهذا ثلاث مشاكل مجتمعة:
 *
 *  ❶ كتابة left/top على عنصر مطلق تُبطل التخطيط، فكانت كل حركة خريطة تعني 500 إعادة
 *    تخطيط للصفحة في الإطار الواحد. التحويل (transform) يبقى على مُركِّب الطبقات ولا
 *    يلمس التخطيط إطلاقاً — وهو ما تفعله طبقة البرق المجاورة أصلاً ولنفس السبب.
 *
 *  ❷ لا سقف ولا تخفيف: عند التبعيد تتراكم مئات البؤر داخل بضعة بكسلات — 500 عنصر لكل
 *    منها `filter: drop-shadow` مزدوج (أغلى ما يُطلب من المُركِّب) لرسم ما يُرى منه
 *    عشرات. التخفيف بأدنى مسافة على الشاشة (الأقوى أوّلاً) يُنقص العدد إلى ما يُرى فعلاً،
 *    فيصير أخفّ وأنظف بصرياً في الوقت نفسه (لا كتلة لهب متلاصقة).
 *
 *  ❸ حدث `render` كان يُسجَّل بدالّة مغلقة ويُلغى بأخرى (`map.off('render', position)`)،
 *    فلا يُلغى شيء: كل تركيب للطبقة يترك مستمعاً دائماً يعمل ستّين مرّة في الثانية إلى
 *    الأبد. المستمع الآن مرجع واحد يُسجَّل ويُلغى.
 */

import { memo, useEffect, useRef } from 'react';
import { cameraKey } from './mapCameraKey';
import { useMapRef } from './MapContext';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { getWildfiresForBounds, type WildfireHotspot } from '../../services/wildfireService';
import { flameAspect, flameUrl, intensityTier } from './wildfireIcons';

/**
 * ارتفاع الأيقونة بدلالة الزووم — **صغيرة كرموز Zoom Earth**: الحريق نقطةٌ على الخريطة
 * لا رسمٌ فوقها. كانت 18 بكسلاً عند z=3 وحتى 76 عند z=11، فغطّت الأيقونة ما حولها،
 * وبدا الحريق الساحلي فوق الماء لأن جسمها يمتدّ عشرات البكسلات من نقطته (2026-09-21).
 * الآن 10 بكسل عند التبعيد و24 أقصى — مقروءة بلا أن تطغى، وأصغر يعني تخفيفاً أقلّ:
 * يظهر من الحرائق المتقاربة أكثر.
 */
function heightForZoom(zoom: number): number {
    return Math.max(10, Math.min(24, 2 + zoom * 2.2));
}

/**
 * سقف العلامات المرسومة، وأدنى مسافة بينها كنسبة من ارتفاع الأيقونة.
 *
 * السقف ليس اعتباطياً: ما فوق ~240 لهباً على شاشة واحدة لا يُقرأ كبؤر منفصلة بل كطبقة
 * برتقالية، وكلفته (تركيب ظلال مرشَّحة) تُحسّ فوراً. والمسافة بنصف ارتفاع الأيقونة تعني
 * أن ما يُحجب بصرياً لا يُرسم أصلاً — وهو ما يجعل التبعيد رخيصاً كالتقريب.
 */
const MAX_MARKERS = 240;

/**
 * ينقل خطّ الطول إلى نسخة العالم المعروضة.
 *
 * ── لماذا ─────────────────────────────────────────────────────────────────────
 * `project` يضع أي إحداثيّ في نسخة العالم **الأساسية** دائماً (‎-180..180). ومع
 * `renderWorldCopies` قد يكون المستخدم ناظراً إلى النسخة المجاورة — شرق خطّ التاريخ
 * أو غربه — فتُرسم البؤرة بعرض عالمٍ كامل عن موضعها، ويتبدّل الانزياح مع كل تغيّر
 * تكبير لأن مركز المنظور ينتقل بين النسخ. رصده المستخدم في 2026-09-20: «عند
 * التكبير والتصغير تتغيّر إحداثيات نفس الحرائق يميناً ويساراً».
 *
 * والأعاصير عولجت من هذا بالضبط (‏unwrapLon في CycloneTracker) وفاتت الحرائق، لأن
 * تعليق العلاج هناك يقول إن «النقاط المفردة لا تحتاجه» — وهو صحيحٌ لعلامات MapLibre
 * التي تضع نفسها، لا لعلامةٍ نحسب موضعها بأنفسنا بـ`project` كما هنا.
 */
function unwrapLon(lon: number, reference: number): number {
    let out = lon;
    while (out - reference > 180) out -= 360;
    while (reference - out > 180) out += 360;
    return out;
}
const SEPARATION_FACTOR = 0.55;

interface Marker {
    el: HTMLElement;
    lon: number;
    lat: number;
    aspect: number;
    tier: number;
}

// بلا props إطلاقاً — memo يمنع إعادة التنفيذ مع كل إعادة رسم لأصله (NewWeatherMap)، وهو
// يُعاد رسمه مع كل نبضة من الشريط الزمني (تشغيل/سحب) لأن currentTimeIndex/frameFraction
// ضمن مصفوفة اشتراكه الواحدة. هذه الطبقة لا تتأثّر بالوقت إطلاقاً (حرائق NASA FIRMS
// تُحدَّث فقط بتحريك الخريطة أو visibleLayers.wildfires عبر اشتراكها المستقلّ)، فإعادة
// تنفيذها كل نبضة عمل مهدور بالكامل — راجع نفس المنطق في CycloneTracker المجاور.
export const WildfireLayer = memo(function WildfireLayer() {
    const mapRef       = useMapRef();
    const {
        visibleLayers,
    } = useWeatherStore(useShallow((s) => ({ visibleLayers: s.visibleLayers })));
    const overlayRef   = useRef<HTMLDivElement | null>(null);
    const hotspotsRef  = useRef<WildfireHotspot[]>([]);
    /** العلامات المرسومة فعلاً (بعد التخفيف) — مجموعة فرعية من hotspotsRef. */
    const markersRef   = useRef<Marker[]>([]);
    /** مخزن عناصر معاد استخدامها: التخفيف يعيد الاختيار مع كل تغيّر زووم، وإنشاء/إتلاف
     *  مئات العناصر في كل مرّة يورّث عمل جامع القمامة إلى إطار الرسم نفسه. */
    const poolRef      = useRef<HTMLElement[]>([]);
    const rebuildRef   = useRef<(() => void) | null>(null);
    /** آخر زووم بُني عليه التخفيف — التخفيف يعتمد على حجم الأيقونة، فالزووم يُبطله. */
    const builtZoomRef = useRef<number | null>(null);
    /** بصمة آخر كاميرا وُضعت عليها العلامات — يمنع العمل على خريطة ساكنة. */
    const lastCamRef   = useRef<string | null>(null);

    // ── Mount overlay ─────────────────────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const container = map.getContainer();
        const overlay   = document.createElement('div');
        overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:9;overflow:hidden;';
        container.appendChild(overlay);
        overlayRef.current = overlay;

        /** يضع العلامات المختارة — تحويلاً لا left/top، فبلا إعادة تخطيط. */
        const position = (force = false) => {
            const mapObj = mapRef.current;
            if (!mapObj) return;
            // خريطة ساكنة ⇒ لا موضع يتغيّر. حدث `render` يُطلق ستّين مرّة في الثانية بلا
            // انقطاع بسبب طلب إعادة الرسم من طبقة الجسيمات، فبلا هذا الحارس كنّا نُسقط
            // كل بؤرة حرارية ونكتب نمطها ستّين مرّة في الثانية بلا أي تغيّر في الصورة.
            const key = cameraKey(mapObj);
            if (!force && key === lastCamRef.current) return;
            lastCamRef.current = key;
            const viewLon = mapObj.getCenter().lng;
            for (const m of markersRef.current) {
                const pt = mapObj.project([unwrapLon(m.lon, viewLon), m.lat]);
                m.el.style.setProperty('--wx', `${Math.round(pt.x)}px`);
                m.el.style.setProperty('--wy', `${Math.round(pt.y)}px`);
            }
        };

        /**
         * يختار ما يُرسم ويبنيه: الأقوى شدّةً أوّلاً، ثم تُرفض كل بؤرة تقع أقرب من مسافة
         * الفصل إلى بؤرة مقبولة — عبر جدول تجزئة مكاني (المربّعات التسعة المجاورة) لا
         * مقارنة الكل بالكل. الترتيب بالشدّة يضمن أن أقوى الحرائق هي الباقية دائماً.
         */
        const rebuild = () => {
            const host = overlayRef.current;
            const mapObj = mapRef.current;
            if (!host || !mapObj) return;

            const zoom = mapObj.getZoom();
            builtZoomRef.current = zoom;
            const h = heightForZoom(zoom);
            const sep = Math.max(8, h * SEPARATION_FACTOR);
            const minDistSq = sep * sep;

            const sorted = hotspotsRef.current.slice().sort((a, b) => b.intensity - a.intensity);
            const bucketOf = (v: number) => Math.floor(v / sep);
            const taken = new Map<string, { x: number; y: number }[]>();
            const picked: WildfireHotspot[] = [];

            const viewLon = mapObj.getCenter().lng;
            for (const hotspot of sorted) {
                if (picked.length >= MAX_MARKERS) break;
                const pt = mapObj.project([unwrapLon(hotspot.lon, viewLon), hotspot.lat]);
                const bx = bucketOf(pt.x), by = bucketOf(pt.y);
                let tooClose = false;
                for (let j = -1; j <= 1 && !tooClose; j++) {
                    for (let i = -1; i <= 1 && !tooClose; i++) {
                        const cell = taken.get(`${bx + i},${by + j}`);
                        if (!cell) continue;
                        for (const p of cell) {
                            const dx = p.x - pt.x, dy = p.y - pt.y;
                            if (dx * dx + dy * dy < minDistSq) { tooClose = true; break; }
                        }
                    }
                }
                if (tooClose) continue;
                const cellKey = `${bx},${by}`;
                const cell = taken.get(cellKey);
                if (cell) cell.push({ x: pt.x, y: pt.y }); else taken.set(cellKey, [{ x: pt.x, y: pt.y }]);
                picked.push(hotspot);
            }

            // إعادة استخدام العناصر: ننمّي المخزن للحاجة فقط، ونُخفي الزائد بلا إتلاف.
            while (poolRef.current.length < picked.length) {
                const el = document.createElement('div');
                // الموضع والمرساة والتكبير عند المرور كلّها في .wildfire-marker عبر متغيّرات
                // CSS — لا نمط inline للتحويل: النمط المضمّن يتفوّق على قاعدة :hover فيقتل
                // تأثير المرور بصمت (الأولوية تحسمها المواصفة لا ترتيب التعريف).
                el.className = 'wildfire-marker';
                el.style.position = 'absolute';
                el.style.backgroundSize = 'contain';
                el.style.backgroundRepeat = 'no-repeat';
                el.style.backgroundPosition = 'center bottom';
                el.style.pointerEvents = 'auto';
                poolRef.current.push(el);
                host.appendChild(el);
            }

            const markers: Marker[] = [];
            for (let i = 0; i < poolRef.current.length; i++) {
                const el = poolRef.current[i];
                const hotspot = picked[i];
                if (!hotspot) { el.style.display = 'none'; continue; }
                const tier = intensityTier(hotspot.intensity);
                const aspect = flameAspect(tier);
                el.style.display = '';
                el.style.backgroundImage = flameUrl(tier);
                el.style.height = `${h}px`;
                el.style.width = `${h * aspect}px`;
                el.title = hotspot.name;
                markers.push({ el, lon: hotspot.lon, lat: hotspot.lat, aspect, tier });
            }
            markersRef.current = markers;
            position(true);
        };

        rebuildRef.current = rebuild;
        rebuild();

        // مستمع واحد بمرجع ثابت — يُلغى فعلاً عند التفكيك (راجع ❸ في رأس الملف).
        const onRender = () => {
            // الزووم يُبطل التخفيف (المسافة مشتقّة من حجم الأيقونة)، فنعيد الاختيار عند
            // تغيّره فعلياً لا عند كل حركة لوحة. وما دون ذلك تحويلٌ محض.
            const z = mapRef.current?.getZoom();
            if (z !== undefined && builtZoomRef.current !== null && Math.abs(z - builtZoomRef.current) > 0.35) {
                rebuild();
                return;
            }
            position();
        };
        map.on('render', onRender);

        return () => {
            map.off('render', onRender);
            rebuildRef.current = null;
            poolRef.current = [];
            markersRef.current = [];
            overlay.remove();
        };
    }, [mapRef]);

    // ── Fetch hotspots (عند التفعيل وعند كل تحريك للخريطة) ────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!visibleLayers.wildfires || !map) {
            hotspotsRef.current = [];
            rebuildRef.current?.();
            return;
        }

        let cancelled = false;
        let seq = 0;   // يهمل ردود الطلبات القديمة إذا تحرّكت الخريطة أثناء انتظار ردّ بطيء
        /*
         * ── طلبٌ بعد أن تستقرّ اليد، ولا طلب لما جُلب سلفاً ────────────────────
         *
         * `moveend` ليس حدثاً واحداً لكل حركة: يقع عند نهاية كل دفعة سحب وكل قصور
         * ذاتي، فالسحب المتّصل يُطلقه عشرات المرّات. وقياس 2026-09-20 على جهاز
         * المستخدم: **تسعون طلباً** لـ`wildfires/region` في تسجيلٍ طوله عشر ثوانٍ،
         * ومجموع `fetch` أحد عشر ألف مللي. هذا هو ثقل الطبقة عند فتحها، لا الرسم.
         *
         * فمهلةٌ قصيرة تبتلع دفعات السحب المتتابعة، وحارسٌ يُسقط الطلب إذا كان
         * المنظور الجديد **داخل** ما جُلب أصلاً — والبيانات تُجلب بهامش، فالتحريك
         * الصغير لا يكشف جديداً.
         */
        let timer = 0;
        let got: { n: number; s: number; e: number; w: number } | null = null;
        const inside = (b: { n: number; s: number; e: number; w: number }) =>
            got !== null && b.n <= got.n && b.s >= got.s && b.e <= got.e && b.w >= got.w;
        const schedule = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(fetchNow, 400);
        };
        const fetchNow = () => {
            const mySeq = ++seq;
            const bounds = map.getBounds();
            const box = {
                n: bounds.getNorth(), s: bounds.getSouth(),
                e: bounds.getEast(), w: bounds.getWest(),
            };
            if (inside(box)) return;                 // داخل ما جُلب — لا جديد يُطلب
            getWildfiresForBounds({
                north: box.n, south: box.s, east: box.e, west: box.w,
            })
                .then(h => {
                    if (cancelled || mySeq !== seq) return;
                    got = box;
                    hotspotsRef.current = h; rebuildRef.current?.();
                })
                .catch(err => console.error('Failed to fetch wildfire data:', err));
        };

        fetchNow();
        map.on('moveend', schedule);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            map.off('moveend', schedule);
        };
    }, [mapRef, visibleLayers.wildfires]);

    return null;
});
